import React, { useState, useEffect } from 'react';
import { Text, View, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import _ from 'lodash';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import FieldError from 'src/presentation/components/atoms/FieldError';
import { ChoiceInfo, ChoiceDetailLink } from 'src/presentation/components/molecules/ProgramChoice';
import { choiceGroups } from 'src/presentation/components/molecules/ProgramChoiceGroups';
import type { ApiFormAnswer, ApiFormQuestion, ApiFormQuestionOption } from 'src/core/models/api/shared/ApiFormQuestion';
import { limitReached } from 'src/core/utils/formChoiceLimits';

interface RadioListFieldProps {
	name: string;
	data: ApiFormQuestion & { show_limit?: boolean };
	answers?: Record<string, ApiFormAnswer>;
	onSwitch: (value: string) => void;
	formID?: string | number;
	error?: string;
}

interface InitialValue {
	value: string;
	hasAnswer: string[];
}

function getInitialValue(props: RadioListFieldProps): InitialValue {
	const { answers, name } = props;
	const existing = answers?.[name];
	if (typeof existing === 'string' && existing.length > 0) {
		return { value: existing, hasAnswer: [name] };
	}
	return { value: '', hasAnswer: [] };
}

function RadioListField(props: RadioListFieldProps) {
	const { data, onSwitch, error } = props;

	const options: ApiFormQuestionOption[] = data.options ?? [];

	const initial = getInitialValue(props);
	const [chosenValue, setChosenValue] = useState<string>(initial.value);
	const [hasAnswer, setHasAnswer] = useState<string[]>(initial.hasAnswer);

	useEffect(() => {
		const updated = getInitialValue(props);
		setChosenValue(updated.value);
		setHasAnswer(updated.hasAnswer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.answers]);

	const toggleRadio = (option: ApiFormQuestionOption) => {
		const alreadyHasAnswer = hasAnswer.includes(option.name);

		// >=, not ==: an option that somehow went past its capacity is full too, and would otherwise be selectable
		// again.
		if (limitReached(option) && !alreadyHasAnswer) return;

		setChosenValue(option.name);
		setHasAnswer([]);

		onSwitch(option.name);
	};

	const renderOption = (option: ApiFormQuestionOption, index: number) => {
		const disabled = limitReached(option) && !hasAnswer.includes(option.name);

		return (
			<TouchableOpacity
				key={index}
				style={[
					{
						flexDirection: 'row',
						paddingHorizontal: Dimens.padding8,
						paddingVertical: Dimens.padding12,
						alignItems: 'center',
						borderBottomWidth: 1,
						borderBottomColor: Colors.greyBorder,
					},
					StyleSheet.flatten(styles.listitem),
				]}
				onPress={() => toggleRadio(option)}
			>
				<View>
					{chosenValue === option.name ? (
						<View style={styles.checked}>
							<View style={styles.innerround} />
						</View>
					) : (
						<View style={styles.notchecked} />
					)}
				</View>

				<View style={{ paddingLeft: Dimens.padding10, flex: 1 }}>
					<Text style={[styles.text, disabled && styles.disabled]}>{option.name}</Text>
					<ChoiceInfo data={data} option={option} withDay={!grouped} />
				</View>

				<ChoiceDetailLink option={option} />
			</TouchableOpacity>
		);
	};

	// A question can draw from several days; then each day gets its own heading and the options sit under it, in
	// the order the beheerder arranged them.
	const groups = choiceGroups(options);
	const grouped = groups.length > 1;

	return (
		<View>
			<View style={[styles.listTitle, { borderBottomWidth: 1, borderBottomColor: Colors.greyBorder }]}>
				<Text style={styles.question}>
					{data.label}
					{data.required ? '*' : null}
				</Text>
			</View>
			{error ? <FieldError message={error} /> : null}
			{groups.map((group, groupIndex) => (
				<View key={groupIndex}>
					{group.label ? <Text style={styles.dayHeading}>{group.label}</Text> : null}
					{group.options.map(renderOption)}
				</View>
			))}
		</View>
	);
}

export default RadioListField;

const styles = StyleSheet.create({
	text: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
	},
	disabled: {
		color: Colors.greyText,
	},
	listitem: {
		marginRight: -20,
		marginLeft: Dimens.margin0,
		borderBottomWidth: Platform.OS === 'android' ? 0 : undefined,
		paddingBottom: Platform.OS === 'android' ? Dimens.padding12 : undefined,
		paddingTop: Platform.OS === 'android' ? Dimens.padding12 : undefined,
	},
	listTitle: {
		// No top padding: the spacing between fields comes from the uniform fieldBlock margin, so a choice question
		// sits at the same distance from the field above it as a text field does.
		paddingTop: Dimens.padding0,
		marginRight: -20,
		marginLeft: Dimens.margin0,
		paddingBottom: Platform.OS === 'android' ? Dimens.padding8 : Dimens.padding8,
		borderBottomWidth: Platform.OS === 'android' ? 1 : undefined,
		borderBottomColor: Platform.OS === 'android' ? Colors.overlay42 : undefined,
		marginBottom: Platform.OS === 'android' ? Dimens.margin8 : undefined,
	},
	dayHeading: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize13,
		color: Colors.greyTextDark,
		paddingTop: Dimens.padding12,
		paddingBottom: Dimens.padding4,
		paddingHorizontal: Dimens.padding8,
	},
	question: {
		fontFamily: Platform.OS === 'android' ? Fonts.montserratRegular : Fonts.montserratBold,
		fontSize: Dimens.fontSize16,
		color: Colors.greyTextDark,
		lineHeight: 20,
	},
	checked: {
		height: Platform.OS === 'android' ? 20 : 15,
		width: Platform.OS === 'android' ? 20 : 15,
		borderWidth: Platform.OS === 'android' ? 2 : 1,
		borderColor: Colors.secondary,
		borderRadius: Platform.OS === 'android' ? 20 : 15,
		alignItems: 'center',
		justifyContent: 'center',
	},
	notchecked: {
		height: Platform.OS === 'android' ? 20 : 15,
		width: Platform.OS === 'android' ? 20 : 15,
		borderWidth: Platform.OS === 'android' ? 2 : 1,
		borderColor: Platform.OS === 'android' ? Colors.greyPlatform : Colors.greyText,
		borderRadius: Platform.OS === 'android' ? 20 : 15,
	},
	innerround: {
		height: Platform.OS === 'android' ? 10 : 6,
		width: Platform.OS === 'android' ? 10 : 6,
		backgroundColor: Platform.OS === 'android' ? Colors.secondary : Colors.secondary,
		borderRadius: Platform.OS === 'android' ? 15 : 8,
	},
});
